import type { ClientResult } from "@/types";

export type UploadClientDependencies = {
  noProgressTimeoutMs: number;
  createRequest?: () => XMLHttpRequest;
  setTimeout?: typeof globalThis.setTimeout;
  clearTimeout?: typeof globalThis.clearTimeout;
};

export type UploadInput = {
  body: Blob;
  contentType: string;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
  url: string;
};

export const createUploadClient = (dependencies: UploadClientDependencies) => {
  const createRequest =
    dependencies.createRequest ?? (() => new XMLHttpRequest());
  const setTimer = dependencies.setTimeout ?? globalThis.setTimeout;
  const clearTimer = dependencies.clearTimeout ?? globalThis.clearTimeout;

  return {
    upload: (input: UploadInput): Promise<ClientResult<void>> => {
      if (input.signal?.aborted) {
        return Promise.resolve({ ok: false, error: "cancelled" });
      }

      return new Promise((resolve) => {
        const request = createRequest();
        let settled = false;
        let stalled = false;
        let watchdog: ReturnType<typeof setTimeout> | undefined;

        const finish = (result: ClientResult<void>) => {
          if (settled) return;
          settled = true;
          if (watchdog !== undefined) clearTimer(watchdog);
          input.signal?.removeEventListener("abort", cancel);
          request.onload = null;
          request.onerror = null;
          request.onabort = null;
          request.ontimeout = null;
          request.upload.onprogress = null;
          resolve(result);
        };

        const armWatchdog = () => {
          if (watchdog !== undefined) clearTimer(watchdog);
          watchdog = setTimer(() => {
            stalled = true;
            request.abort();
          }, dependencies.noProgressTimeoutMs);
        };

        const cancel = () => request.abort();
        /* action-id: lifecycle.upload.cancelled */ input.signal?.addEventListener(
          "abort",
          cancel,
          { once: true }
        );

        request.upload.onprogress = (event) => {
          armWatchdog();
          if (event.lengthComputable && event.total > 0) {
            input.onProgress?.(
              Math.round((event.loaded / event.total) * 100)
            );
          }
        };
        request.onload = () =>
          finish(
            request.status >= 200 && request.status < 300
              ? { ok: true, value: undefined }
              : request.status === 401 || request.status === 403
                ? { ok: false, error: "uploadAuthorizationExpired" }
                : { ok: false, error: "uploadRejected" }
          );
        request.onerror = () =>
          finish({ ok: false, error: "networkUnavailable" });
        request.ontimeout = () => finish({ ok: false, error: "timeout" });
        request.onabort = () =>
          finish({
            ok: false,
            error: stalled ? "timeout" : "cancelled",
          });

        request.open("PUT", input.url);
        request.setRequestHeader("Content-Type", input.contentType);
        armWatchdog();
        request.send(input.body);
      });
    },
  };
};

export type UploadClient = ReturnType<typeof createUploadClient>;
